import { ref } from 'vue';
import AuctionRepository from '@/repositories/auction/AuctionRepository.ts';
import {
    Auction,
    AuctionBid,
    AuctionBidCreateForm,
    AuctionCarMonthCounter,
    AuctionCreateForm,
    AuctionSetDeliveryRequiredForm,
    AuctionsPaginator,
    AuctionsRequestFilter,
    AuctionUpdateForm,
    CarLastAuctionListItem,
    JoiningAuctionsRequestFilter,
} from '@/types/auction.type.ts';
import { PaginationMeta } from '@/types/pagination-meta.ts';
import useLoading from '@/utils/useLoading.ts';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function useAuctionRepository() {
    const arAuctions = ref<Auction[]>([]);
    const auctionsPaginationMeta = ref<PaginationMeta | null>(null);
    const { isLoading: isAuctionsLoading, setLoading: setAuctionsLoading } = useLoading();

    const arJoiningAuctions = ref<Auction[]>([]);
    const joiningAuctionsPaginationMeta = ref<PaginationMeta | null>(null);
    const { isLoading: isJoiningAuctionsLoading, setLoading: setJoiningAuctionsLoading } = useLoading();

    const auctionRepository = new AuctionRepository();

    function getLastAuctionByCarsIds(car_ids: number[]): Promise<CarLastAuctionListItem[]> {
        return auctionRepository.getLastAuctionByCarsIds(car_ids);
    }

    function getAuctions(requestFilter: AuctionsRequestFilter): void {
        setAuctionsLoading(true);

        auctionRepository.getAuctions(requestFilter)
            .then((auctionsPaginator: AuctionsPaginator) => {
                arAuctions.value = auctionsPaginator.data;
                auctionsPaginationMeta.value = auctionsPaginator.meta;
                setAuctionsLoading(false);
            });
    }

    function forceCompleteAuction(id: number): Promise<void> {
        return auctionRepository.forceCompleteAuction(id);
    }

    function prolongAuction(id: number, minutes: number): Promise<void> {
        return auctionRepository.prolongAuction(id, minutes);
    }

    function createAuction(form: AuctionCreateForm): Promise<void> {
        return auctionRepository.createAuction(form);
    }

    function getAuction(id: number): Promise<Auction> {
        return auctionRepository.getAuction(id);
    }

    function updateAuction(id: number, form: AuctionUpdateForm): Promise<void> {
        return auctionRepository.updateAuction(id, form);
    }

    function publishAuction(id: number): Promise<void> {
        return auctionRepository.publishAuction(id);
    }

    function deleteAuction(id: number): Promise<void> {
        return auctionRepository.deleteAuction(id);
    }

    function sendAnnouncementAboutAuction(id: number): Promise<void> {
        return auctionRepository.sendAnnouncementAboutAuction(id);
    }

    function annulAuction(id: number): Promise<void> {
        return auctionRepository.annulAuction(id);
    }

    function createAuctionBid(id: number, form: AuctionBidCreateForm): Promise<void> {
        return auctionRepository.createAuctionBid(id, form);
    }

    function getAuctionBids(id: number): Promise<AuctionBid[]> {
        return auctionRepository.getAuctionBids(id);
    }

    function confirmAuctionWinner(id: number): Promise<void> {
        return auctionRepository.confirmAuctionWinner(id);
    }

    function setAuctionDeliveryRequired(id: number, form: AuctionSetDeliveryRequiredForm): Promise<void> {
        return auctionRepository.setAuctionDeliveryRequired(id, form);
    }

    function getJoiningAuctions(requestFilter: JoiningAuctionsRequestFilter): void {
        setJoiningAuctionsLoading(true);

        auctionRepository.getJoiningAuctions(requestFilter)
            .then((auctionsPaginator: AuctionsPaginator) => {
                arJoiningAuctions.value = auctionsPaginator.data.map((auction: Auction) => {
                    auction.user_joining = true;

                    return auction;
                });
                joiningAuctionsPaginationMeta.value = auctionsPaginator.meta;
                setJoiningAuctionsLoading(false);
            });
    }

    function getAuctionCountByCarsIdsOnCurrentMonth(carIds: number[]): Promise<AuctionCarMonthCounter[]> {
        return auctionRepository.getAuctionCountByCarIdOnCurrentMonth(carIds);
    }

    function getAuctionCountByCarIdOnCurrentMonth(carId: number): Promise<number> {
        return getAuctionCountByCarsIdsOnCurrentMonth([carId]).then((counters: AuctionCarMonthCounter[]) => {
            return counters[0].count;
        });
    }

    return {
        arAuctions,
        auctionsPaginationMeta,
        isAuctionsLoading,
        arJoiningAuctions,
        joiningAuctionsPaginationMeta,
        isJoiningAuctionsLoading,
        getLastAuctionByCarsIds: getLastAuctionByCarsIds,
        getAuctions: getAuctions,
        forceCompleteAuction: forceCompleteAuction,
        prolongAuction: prolongAuction,
        createAuction: createAuction,
        getAuction: getAuction,
        updateAuction: updateAuction,
        publishAuction: publishAuction,
        deleteAuction: deleteAuction,
        sendAnnouncementAboutAuction: sendAnnouncementAboutAuction,
        annulAuction: annulAuction,
        createAuctionBid: createAuctionBid,
        getAuctionBids: getAuctionBids,
        confirmAuctionWinner: confirmAuctionWinner,
        setAuctionDeliveryRequired: setAuctionDeliveryRequired,
        getJoiningAuctions: getJoiningAuctions,
        getAuctionCountByCarsIdsOnCurrentMonth: getAuctionCountByCarsIdsOnCurrentMonth,
        getAuctionCountByCarIdOnCurrentMonth: getAuctionCountByCarIdOnCurrentMonth,
    };
}
